import { AppComponent } from '../../app.component';
import { QueryBuilderClassNames } from '../../shared/query-builder/query-builder.interfaces';
import { MessageHandlerService } from '../../core/utility/message-handler.service';
import { Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FieldConfigurationService } from '../../core/service/field-configuration.service';
import { QueryBuilderService } from '../../shared/query-builder/query-builder.service';
import { isArray, isEmpty, transform, isObject, clone, filter, find, differenceWith, isEqual, cloneDeep } from 'lodash';
import { NGXLogger } from 'ngx-logger';
import { DeactivationGuarded } from 'src/app/core/routing/can-deactivate-guard.service';
import { Observable } from 'rxjs';
import { ChannelService } from 'src/app/core/service/channel.service';
import { Channel } from 'src/app/channel/model/channel';
import { caretAltDownIcon } from "@progress/kendo-svg-icons";
import { RuleService } from 'src/app/core/service/rule.service';

@Component({
  selector: 'app-channel-rule',
  templateUrl: './channel-rule.component.html',
  styleUrls: ['./channel-rule.component.scss']
})
export class ChannelRuleComponent implements OnInit, DeactivationGuarded
{
  @Input() public channel: Channel;

  public classNames: QueryBuilderClassNames;
  public queries: any[] = [];
  public loadedQueries: any[] = [];
  public configs: any[] = [];
  public mainSections: any[] = [];
  public subSections: any[] = [];
  public subSectionsDropDown: any[] = [];
  public id: number;
  public ruleSetId: number;
  public isValidChannel = false;
  public openedRuleTester = false;
  public ruleSetDetails: any = null;
  public icons = { caretDown: caretAltDownIcon };

  constructor(
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private fieldConfigurationService: FieldConfigurationService,
    private queryBuilderService: QueryBuilderService,
    private route: ActivatedRoute,
    private router: Router,
    private appComponent: AppComponent,
    private channelService: ChannelService,
    private ruleService: RuleService)
  {
    this.loadRuleMainSections();
    this.loadRuleSubSections();
    this.loadFieldConfigurations();
  }

  public ngOnInit(): void
  {
    this.initChannelRules();

    // Check if a resource identifier is present
    this.route.params.forEach((params: Params) =>
    {
      if (params.id !== undefined)
      {
        this.id = +params.id;
        this.ruleSetId = +params.ruleSetId;

        // Retrieve the channel by id and bind to the view model
        this.channelService.getChannel(this.id).subscribe({
          next: (data) =>
          {
            this.isValidChannel = true;
            this.channel = data;
            this.loadChannelRules(this.id, this.ruleSetId);
            this.setRuleSetDetails(this.channel, this.ruleSetId);
          },
          error: (errorResponse) =>
          {
            // For 404, display a more appropriate message
            if (errorResponse && errorResponse.status === 404)
            {
              const optionsOverride = { disableTimeOut: true };
              this.messageHandlerService.displayError('RULE-PROGRAM-LOAD-ERROR', 'GENERIC-ERROR-MESSAGE-TITLE', optionsOverride);
            }
            else
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          }
        });
      }
    });
  }

  /**
   * canDesactivate event implementation for monitoring if dirty changes and navigation without saving
   */
  public canDeactivate(): boolean | Observable<boolean> | Promise<boolean>
  {
    const diff = differenceWith(this.loadedQueries, this.queries, isEqual);
    const isDirty = diff.length > 0;

    if (isDirty)
    {
      this.appComponent.openUnsavedDialog();
      return this.appComponent.navigateAwaySelection;
    }
    else
    {
      return true;
    }
  }

  /**
   * Event called when sub-section is selected from the drop down selection
   *
   * @param event click event
   */
  public onSubSectionClick(event): void
  {
    for (let i = 0; i < this.subSections.length; i++)
    {
      if (this.subSections[i].value === event.value)
      {
        this.subSections[i].selected = true;
        break;
      }
    }
    this.initSubSectionsDropDown();
  }

  public onSubmit(event: Event, navigateToChannel: boolean): void
  {
    this.saveChannelRules(navigateToChannel);
  }

  public backToChannelEdit(): void
  {
    this.router.navigateByUrl(`/channel-edit/${this.id}`);
  }

  /**
   * Disable buttons based on channel status.
   */
  public disableFormControl(): boolean
  {
    return this.channel.status === 'Deleted' || this.channel.status === 'Archived' || !this.channel.active;
  }

  /**
   * Manually remove the sub-section when there is no more rules. This will be done automatically on save too.
   */
  public removeSubSection(index: number): void
  {
    this.subSections[index].selected = false;
    this.initSubSectionsDropDown();
  }

  /**
   * Open the Rule Tester modal.
   */
  public openRuleTester(): void
  {
    this.openedRuleTester = true;
  }

  /**
   * Close the Rule Tester modal.
   */
  public closeRuleTester(): void
  {
    this.openedRuleTester = false;
  }

  /**
   * Check if the query builder has some rules defined. This is to set the disabled state of the Rule Tester button.
   *
   * @returns boolean Returns true if builder contains any conditions.
   */
  public ruleTesterEnabled(): boolean
  {
    let rulesCount = 0;
    for (let i = 0; i < this.queries.length; i++)
    {
      if (this.queries[i].rules && this.queries[i].rules.length > 0)
      {
        rulesCount += this.queries[i].rules.length;
        break;
      }
    }
    return rulesCount > 0;
  }

  /**
   * Check if the query rule is valid
   *
   * @param query rule object
   */
  private isValidRules(query): boolean
  {
    let emptyRulesFound = false;
    const hasEmptyRulesDeep = (obj) => transform(obj, (result: any, value, key: any) =>
    {
      if (isObject(value))
      {
        value = hasEmptyRulesDeep(value);
      }
      if (key === 'rules')
      {
        if (obj[key].length === 0)
        {
          emptyRulesFound = true;
          if (isArray(obj))
          {
            result.push(value);
          }
          else
          {
            result[key] = value;
          }
        }
      }
    });
    hasEmptyRulesDeep(query);
    return !emptyRulesFound;
  }

  /**
   * loadForm get called everytime the form opens. This allow reloading the rules and XML from storage.
   */
  private loadFieldConfigurations(): void
  {
    this.fieldConfigurationService.getCachedFieldConfigurations(true).subscribe({
      next: (data) =>
      {
        this.fieldConfigurationService.getCodeSetCodesUsedByFieldConfigurations(data).subscribe({
          next: (codeSetCodes) =>
          {
            // Replacing options with loaded Code Sets
            this.fieldConfigurationService.setCodeSetCodesUsedByFieldConfigurations(data, codeSetCodes);

            // Create config per section/sub-section
            for (let i = 0; i < this.mainSections.length; i++)
            {
              const filtered = filter(data, { section: this.mainSections[i].value });
              this.configs[i] = this.queryBuilderService.transform(filtered);
              this.mainSections[i].hidden = filtered.length === 0;

              // Set subSections
              if (this.mainSections[i].value === 'ClinicalHistory')
              {
                for (let j = 0; j < this.subSections.length; j++)
                {
                  const filteredSubSection = filter(data, { subSection: this.subSections[j].value });
                  this.configs[i + j] = this.queryBuilderService.transform(filteredSubSection);
                  this.subSections[j].hidden = filteredSubSection.length === 0;
                }
              }
            }
            this.classNames = this.queryBuilderService.classNames();
            this.initSubSectionsDropDown();
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
        this.logger.error(error);
      }
    });
  }

  /**
   * Initialization of the main sections using the query builder service
   */
  private loadRuleMainSections(): void
  {
    this.mainSections = clone(this.queryBuilderService.mainSections);
  }

  /**
   * Initialization of the sub-sections using the query builder service
   */
  private loadRuleSubSections(): void
  {
    this.subSections = clone(this.queryBuilderService.subSections);
  }

  /**
   * This method clean the query rules and set the query property.
   * It must be cleaned to remove empty rules array which cause component failure.
   *
   * @param data query rules data object
   */
  private setChannelRules(data: any): void
  {
    // Omit deep function to cleanup empty rules array
    const omitDeep = (obj: any, predicate: any) => transform(obj, (result, value, key: any) =>
    {
      if (isObject(value))
      {
        value = omitDeep(value, predicate);
      }
      else
      {
        result[key] = value;
      }
      const doOmit = predicate(value, key) && key !== 'condition';
      if (!doOmit)
      {
        result[key] = value;
      }
    });
    // Load data into proper sections/sub-sections
    for (let i = 0; i < this.queries.length; i++)
    {
      if (this.queries[i].section === data.section && this.queries[i].subSection === data.subSection)
      {
        const cleanQuery: any = omitDeep(data, isEmpty);
        if (cleanQuery.rules === undefined)
        {
          cleanQuery.rules = [];
        }
        this.queries[i] = cleanQuery;
        this.setSubSectionSelected(cleanQuery);
        break;
      }
    }
  }

  /**
   * Initialization of the queries object for all sections/sub-sections
   */
  private initChannelRules()
  {
    const DEFAULT_CONDITION = 'and';
    for (let i = 0; i < this.mainSections.length; i++)
    {
      this.queries[i] = {
        condition: DEFAULT_CONDITION, rules: [],
        section: this.mainSections[i].value, subSection: null
      };
      if (this.mainSections[i].value === 'ClinicalHistory')
      {
        for (let j = 0; j < this.subSections.length; j++)
        {
          this.queries[i + j] = {
            condition: DEFAULT_CONDITION, rules: [],
            section: this.mainSections[i].value, subSection: this.subSections[j].value
          };
          this.setSubSectionSelected(this.queries[i + j]);   // In order to reset selected on new channel
        }
      }
    }
  }

  /**
   * Set the RuleSet details based on loaded Channel RuleSet
   */
  private setRuleSetDetails(channel: Channel, ruleSetId: number): void
  {
    if (channel.ruleSets)
    {
      this.ruleSetDetails = find(channel.ruleSets, { id: ruleSetId });
    }
    else
    {
      this.ruleSetDetails = null;
    }
  }

  /**
   * Load Channel rules
   */
  private loadChannelRules(channelId: number, ruleSetId: number): void
  {
    this.initChannelRules();

    this.ruleService.getQueryRuleByChannel(channelId, ruleSetId).subscribe({
      next: (data) =>
      {
        if (data)
        {
          for (let i = 0; i < data.length; i++)
          {
            // Transform Rules value String to Array
            this.ruleService.convertRulesValueStringToArray(data[i]);
            this.setChannelRules(data[i]);
          }
        }
        this.initSubSectionsDropDown();
      },
      error: error =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.loadedQueries = cloneDeep(this.queries);
      }
    });
  }

  /**
   * Save Channel rules
   */
  private saveChannelRules(navigateToChannel: boolean): void
  {
    for (let i = 0; i < this.queries.length; i++)
    {
      const isNewQueryRule = (this.queries[i].version === undefined);

      // Don't save if not visible section
      if (!this.isVisibleSection(this.queries[i])) { continue; }

      // Don't save if new and empty rules
      if (isNewQueryRule && this.queries[i].rules && this.queries[i].rules.length === 0) { continue; }

      this.queries[i].channel = { id: this.id };
      this.queries[i].ruleSet = { id: this.ruleSetId };

      // Before save, convert rules value array to comma delimited string
      this.ruleService.convertRulesValueArrayToString(this.queries[i]);

      this.ruleService.saveQueryRule(this.queries[i], isNewQueryRule).subscribe({
        next: (savedRules) =>
        {
          if (navigateToChannel)
          {
            this.backToChannelEdit();
          }
          else
          {
            // Transform Rules value String to Array
            this.ruleService.convertRulesValueStringToArray(savedRules);
            this.setChannelRules(savedRules);
            this.setSubSectionSelected(savedRules);
            this.initSubSectionsDropDown();
          }
        },
        error: errorResponse =>
        {
          this.messageHandlerService.displayError(errorResponse);
        },
        complete: () =>
        {
          this.loadedQueries = cloneDeep(this.queries);
        }
      });
    }
    this.messageHandlerService.displaySuccess('RULES-SAVED');
  }

  /**
   * Set the selected property of the subSections based on the rules array
   */
  private setSubSectionSelected(query)
  {
    for (let i = 0; i < this.subSections.length; i++)
    {
      if (this.subSections[i].value === query.subSection)
      {
        this.subSections[i].selected = query.rules !== undefined && query.rules.length > 0 && query.id > 0;
        break;
      }
    }
  }

  /**
   * Check visibility of the section/sub-section based on the query
   *
   * @param query rule
   */
  private isVisibleSection(query): boolean
  {
    let visible = false;
    for (let i = 0; i < this.mainSections.length; i++)
    {
      if (this.mainSections[i].value === query.section)
      {
        if (this.mainSections[i].value === 'ClinicalHistory')
        {
          for (let j = 0; j < this.subSections.length; j++)
          {
            if (this.subSections[j].value === query.subSection)
            {
              visible = !this.subSections[j].hidden && this.subSections[j].selected;
              break;
            }
          }
        }
        else
        {
          visible = !this.mainSections[i].hidden;
        }
        break;
      }
    }
    return visible;
  }

  /**
   * Initialization of the sub-section drop down based on the all available sub-section which are not hidden
   * or not already selected by the user.
   */
  private initSubSectionsDropDown(): void
  {
    this.subSectionsDropDown = filter(this.subSections, (x) =>
    {
      if (x.hidden || x.selected)
      {
        return null;
      }
      else
      {
        return x;
      }
    });
  }
}
